import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useAuth } from '../../contexts/AuthContext';
import { authUtils } from '../../utils/auth';
import { API_ENDPOINTS } from '../../config/api';
import axios from 'axios';
import ChatMenu from '../chat/ChatMenu';
import JobDetailsModal from './JobDetailsModal';
import AddJobModal from './AddJobModal';

interface GeneratedDocument {
  cv_link: string;
  cover_letter_link: string;
  generated_at: string;
}

interface HistoryEntry {
  timestamp: string;
  status: string;
}

interface JobWithHistory {
  job_title: string;
  job_url: string;
  company_name: string;
  history: HistoryEntry[];
  generated_documents?: GeneratedDocument;
  applied_by?: 'user' | 'bot';
}

interface JobCard {
  id: string;
  job_title: string;
  job_url: string;
  company_name: string;
  status: string;
  history: HistoryEntry[];
  generated_documents?: GeneratedDocument;
  applied_by?: 'user' | 'bot';
}

type StatusType = 'Viewed' | 'Applied' | 'Interview' | 'Offer' | 'Rejected';

interface Column {
  id: StatusType;
  title: string;
  cards: JobCard[];
}

const JobTracking: React.FC = () => {
  const [columns, setColumns] = useState<Record<StatusType, Column>>({
    Viewed: { id: 'Viewed', title: 'Viewed', cards: [] },
    Applied: { id: 'Applied', title: 'Applied', cards: [] },
    Interview: { id: 'Interview', title: 'Interview', cards: [] },
    Offer: { id: 'Offer', title: 'Offer', cards: [] },
    Rejected: { id: 'Rejected', title: 'Rejected', cards: [] },
  });
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<JobCard | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalColumn, setAddModalColumn] = useState<StatusType>('Viewed');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { userName } = useAuth();

  useEffect(() => {
    loadJobsAndOpenDeepLink();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadJobsAndOpenDeepLink = async () => {
    const newColumns = await loadJobs();
    const targetJobUrl = sessionStorage.getItem('open_job_url');
    if (targetJobUrl && newColumns) {
      sessionStorage.removeItem('open_job_url');
      for (const col of Object.values(newColumns)) {
        const found = col.cards.find(c => c.job_url === targetJobUrl);
        if (found) {
          setSelectedJob(found);
          setShowDetailsModal(true);
          break;
        }
      }
    }
  };

  const loadJobs = async (): Promise<Record<StatusType, Column> | null> => {
    try {
      setLoading(true);
      const token = authUtils.getToken();
      const response = await axios.get(`${API_ENDPOINTS.JOB_TRACKER.BASE_URL}/api/jobs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success && response.data.data) {
        const jobs: JobWithHistory[] = response.data.data;
        const newColumns: Record<StatusType, Column> = {
          Viewed: { id: 'Viewed', title: 'Viewed', cards: [] },
          Applied: { id: 'Applied', title: 'Applied', cards: [] },
          Interview: { id: 'Interview', title: 'Interview', cards: [] },
          Offer: { id: 'Offer', title: 'Offer', cards: [] },
          Rejected: { id: 'Rejected', title: 'Rejected', cards: [] },
        };

        jobs.forEach((job) => {
          if (job.history && job.history.length > 0) {
            const latestStatus = job.history[job.history.length - 1].status;

            // Type guard to ensure latestStatus is a valid StatusType
            if (latestStatus in newColumns) {
              const jobCard: JobCard = {
                id: job.job_url,
                job_title: job.job_title,
                job_url: job.job_url,
                company_name: job.company_name,
                status: latestStatus,
                history: job.history,
                generated_documents: job.generated_documents,
                applied_by: job.applied_by,
              };

              newColumns[latestStatus as StatusType].cards.push(jobCard);
            }
          }
        });

        // Sort cards in each column by latest timestamp (newest first)
        Object.keys(newColumns).forEach((columnKey) => {
          const column = newColumns[columnKey as StatusType];
          column.cards.sort((a, b) => {
            const aLatestTime = new Date(a.history[a.history.length - 1].timestamp).getTime();
            const bLatestTime = new Date(b.history[b.history.length - 1].timestamp).getTime();
            return bLatestTime - aLatestTime; // Descending order (newest first)
          });
        });

        setColumns(newColumns);
        return newColumns;
      }
      return null;
    } catch (error) {
      console.error('Failed to load jobs:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const saveJobStatus = async (job: JobCard, newStatus: string, cvLink?: string, coverLetterLink?: string) => {
    try {
      const token = authUtils.getToken();
      const payload: any = {
        job_title: job.job_title,
        job_url: job.job_url,
        company_name: job.company_name,
        status: newStatus,
      };
      
      if (cvLink !== undefined) {
        payload.cv_link = cvLink;
      }
      if (coverLetterLink !== undefined) {
        payload.cover_letter_link = coverLetterLink;
      }
      
      await axios.post(
        `${API_ENDPOINTS.JOB_TRACKER.BASE_URL}/api/jobs`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.error('Failed to save job status:', error);
      throw error;
    }
  };

  const deleteJob = async (jobUrl: string) => {
    try {
      const token = authUtils.getToken();
      await axios.delete(`${API_ENDPOINTS.JOB_TRACKER.BASE_URL}/api/jobs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          job_url: jobUrl,
        },
      });
      await loadJobs();
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  };

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    if (!destination || source.droppableId === destination.droppableId) {
      return;
    }

    const sourceColumnId = source.droppableId as StatusType;
    const destColumnId = destination.droppableId as StatusType;
    
    const sourceColumn = columns[sourceColumnId];
    const destColumn = columns[destColumnId];
    const draggedCard = sourceColumn.cards.find((card) => card.id === draggableId);

    if (!draggedCard) return;

    // Optimistically update the UI immediately
    const newSourceCards = [...sourceColumn.cards];
    newSourceCards.splice(source.index, 1);

    const updatedCard = {
      ...draggedCard,
      status: destColumnId,
      history: [
        ...draggedCard.history,
        {
          timestamp: new Date().toISOString(),
          status: destColumnId,
        },
      ],
    };

    // Insert the card at the correct position in destination column
    const newDestCards = [...destColumn.cards];
    newDestCards.splice(destination.index, 0, updatedCard);

    const newColumns = {
      ...columns,
      [sourceColumnId]: {
        ...sourceColumn,
        cards: newSourceCards,
      },
      [destColumnId]: {
        ...destColumn,
        cards: newDestCards,
      },
    };

    // Update UI immediately for smooth UX
    setColumns(newColumns);

    // Save to backend asynchronously, preserving generated documents
    try {
      const cvLink = draggedCard.generated_documents?.cv_link;
      const coverLetterLink = draggedCard.generated_documents?.cover_letter_link;
      await saveJobStatus(draggedCard, destColumnId, cvLink, coverLetterLink);
    } catch (error) {
      console.error('Failed to update job status:', error);
      // Revert the UI change on error
      setColumns(columns);
    }
  };

  const handleJobClick = (job: JobCard) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const handleSaveGeneratedDocuments = async (jobCard: JobCard, cvLink: string, coverLetterLink: string) => {
    // Save to backend without changing status
    await saveJobStatus(jobCard, jobCard.status, cvLink, coverLetterLink);
    
    // Update local state with generated documents instead of reloading
    const generatedDocuments = {
      cv_link: cvLink,
      cover_letter_link: coverLetterLink,
      generated_at: new Date().toISOString(),
    };
    
    // Update the job card in the current state
    setColumns(prevColumns => {
      const newColumns = { ...prevColumns };
      for (const columnKey of Object.keys(newColumns)) {
        const column = newColumns[columnKey as StatusType];
        const cardIndex = column.cards.findIndex(card => card.id === jobCard.id);
        if (cardIndex !== -1) {
          const updatedCard = { ...column.cards[cardIndex], generated_documents: generatedDocuments };
          newColumns[columnKey as StatusType] = {
            ...column,
            cards: column.cards.map((card, index) => 
              index === cardIndex ? updatedCard : card
            )
          };
          
          // Also update selectedJob if it's the same job
          if (selectedJob && selectedJob.id === jobCard.id) {
            setSelectedJob(updatedCard);
          }
          break;
        }
      }
      return newColumns;
    });
  };

  const handleAddJob = async (jobData: {
    job_title: string;
    job_url: string;
    company_name: string;
    status: string;
  }) => {
    try {
      const token = authUtils.getToken();
      await axios.post(
        `${API_ENDPOINTS.JOB_TRACKER.BASE_URL}/api/jobs`,
        jobData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Reload jobs to get updated data with proper sorting
      await loadJobs();
    } catch (error) {
      console.error('Failed to add job:', error);
      throw error;
    }
  };

  const openAddModal = (columnId: StatusType) => {
    setAddModalColumn(columnId);
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-jobesta-gray-30">
        <div className="text-jobesta-gray-70">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-jobesta-gray-30">
      <ChatMenu />
      
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-x-auto p-6">
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-6 h-full" style={{ minWidth: 'calc(5 * 18rem + 4 * 1.5rem)' }}>
            {Object.values(columns).map((column) => (
              <div
                key={column.id}
                className="bg-white rounded-jobesta shadow-jobesta-card border border-jobesta-gray-40 w-72 flex-shrink-0 flex flex-col"
              >
                <div className="p-4 border-b border-jobesta-gray-40 bg-jobesta-gray-30">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-jobesta-gray-80 uppercase tracking-wide">
                      {column.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-jobesta-gray-60 bg-white px-2 py-1 rounded-full font-medium">
                        {column.cards.length}
                      </span>
                      <button
                        onClick={() => openAddModal(column.id)}
                        className="text-jobesta-gray-60 hover:text-jobesta-blue hover:bg-white p-1 rounded transition-colors"
                        title={`Add job to ${column.title}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <Droppable droppableId={column.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 p-2 overflow-y-auto ${
                        snapshot.isDraggingOver ? 'bg-jobesta-blue bg-opacity-5' : ''
                      }`}
                    >
                      {column.cards.map((card, index) => (
                        <Draggable key={card.id} draggableId={card.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`mb-2 p-3 bg-white rounded-jobesta border border-jobesta-gray-40 cursor-pointer hover:shadow-md transition-shadow ${
                                snapshot.isDragging ? 'shadow-lg opacity-90' : ''
                              }`}
                              onClick={() => handleJobClick(card)}
                            >
                              <h3 className="font-medium text-jobesta-gray-90 text-sm mb-1">
                                {card.job_title}
                              </h3>
                              <p className="text-xs text-jobesta-gray-60">
                                {card.company_name}
                              </p>
                              {card.applied_by === 'bot' && (
                                <span
                                  className="mt-2 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700"
                                  title="This application was submitted by the auto-apply bot"
                                >
                                  🤖 Auto-applied
                                </span>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
        </div>
      </div>

      {showDetailsModal && selectedJob && (
        <JobDetailsModal
          job={selectedJob}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedJob(null);
          }}
          onDelete={async () => {
            if (selectedJob) {
              await deleteJob(selectedJob.job_url);
              setShowDetailsModal(false);
              setSelectedJob(null);
            }
          }}
          onSaveGeneratedDocuments={handleSaveGeneratedDocuments}
        />
      )}

      {showAddModal && (
        <AddJobModal
          defaultStatus={addModalColumn}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddJob}
        />
      )}
    </div>
  );
};

export default JobTracking;